import { ONYX_APP_HEADER, resolveSocket2Url } from '../api/ZionServer.ts'

/** Attesa massima senza eventi prima di considerare il tunnel inattivo. */
const TUNNEL_IDLE_TIMEOUT_MS = 60_000
/** Tentativi di riconnessione SSE prima di errore bloccante. */
const TUNNEL_RETRY_MAX = 3
/** Pausa tra un retry e il successivo. */
const TUNNEL_RETRY_DELAY_MS = 1500

async function syncFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-ONYX-App': ONYX_APP_HEADER,
    ...(init.headers as Record<string, string> | undefined),
  }
  delete headers['Origin']
  delete headers['origin']
  return fetch(resolveSocket2Url(path), {
    ...init,
    headers,
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  })
}

/**
 * Safari: `res.json()` su body non-JSON → SyntaxError "The string did not match the expected pattern".
 * Usa text + JSON.parse controllato.
 */
async function readJsonSafe<T>(res: Response): Promise<T | null> {
  const text = await res.text()
  if (!text.trim()) return null
  try {
    return JSON.parse(text) as T
  } catch (err) {
    console.error('[ZION device-sync] JSON parse failed', err, text.slice(0, 120))
    return null
  }
}

export async function createDeviceSyncSession(
  receiverDeviceId: string,
  receiverEphemeralPubKey: string,
): Promise<{ sessionId: string; expiresAt: number }> {
  const maxAttempts = 2
  let lastErr: unknown = null

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await syncFetch('/device-sync/create', {
        method: 'POST',
        body: JSON.stringify({
          receiverDeviceId,
          receiverEphemeralPubKey,
        }),
      })
      if (!res.ok) {
        lastErr = new Error(`device_sync_create_http_${res.status}`)
        continue
      }
      const data = await readJsonSafe<{ sessionId?: string; expiresAt?: number }>(res)
      const sessionId = data?.sessionId?.trim()
      if (!sessionId) {
        lastErr = new Error('device_sync_create_failed')
        continue
      }
      return { sessionId, expiresAt: Number(data?.expiresAt) || Date.now() + 600_000 }
    } catch (err) {
      // Safari: SyntaxError "expected pattern" su JSON/crypto — non propagare al form Auth.
      console.error(`[ZION] DeviceSync.create attempt ${attempt}/${maxAttempts}`, err)
      lastErr = err
    }
  }

  const msg =
    lastErr instanceof Error
      ? lastErr.message
      : lastErr
        ? String(lastErr)
        : 'device_sync_create_failed'
  throw new Error(msg.includes('pattern') || msg.includes('SyntaxError') ? 'device_sync_create_failed' : msg)
}

export type DeviceSyncSessionInfo = {
  sessionId: string
  receiverEphemeralPubKey: string
  receiverDeviceId: string
  expiresAt: number
}

export async function fetchDeviceSyncSessionInfo(
  sessionId: string,
): Promise<DeviceSyncSessionInfo | null> {
  const sid = sessionId.trim()
  if (!sid) return null
  const res = await syncFetch(`/device-sync/${encodeURIComponent(sid)}/info`)
  if (!res.ok) return null
  const data = await readJsonSafe<Record<string, unknown>>(res)
  if (!data) return null
  const pubKey = String(data.receiverEphemeralPubKey ?? '').trim()
  const deviceId = String(data.receiverDeviceId ?? '').trim()
  const expiresAt = Number(data.expiresAt) || 0
  if (!pubKey || !deviceId || !expiresAt) return null
  return {
    sessionId: sid,
    receiverEphemeralPubKey: pubKey,
    receiverDeviceId: deviceId,
    expiresAt,
  }
}

export type DeviceSyncListenHandlers = {
  onStatus?: (status: string) => void
  onVault?: (encryptedB64: string, senderEphemeralPubKey: string) => void
  onError?: (msg: string) => void
}

function parseListenMessage(raw: string): Record<string, unknown> | null {
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Ascolta SSE sul canale temporaneo di pairing.
 * Timeout lungo, retry automatico su disconnessione prematura, ignore close post-vault.
 */
export function listenDeviceSyncSession(
  sessionId: string,
  handlers: DeviceSyncListenHandlers,
): () => void {
  let stopped = false
  let vaultReceived = false
  let retryCount = 0
  let es: EventSource | null = null
  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const clearTimers = (): void => {
    if (idleTimer) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  const closeEventSource = (): void => {
    if (es) {
      es.close()
      es = null
    }
  }

  const bumpIdleTimeout = (): void => {
    if (idleTimer) clearTimeout(idleTimer)
    if (stopped || vaultReceived) return
    idleTimer = setTimeout(() => {
      if (!stopped && !vaultReceived) scheduleReconnect('tunnel_idle_timeout')
    }, TUNNEL_IDLE_TIMEOUT_MS)
  }

  const scheduleReconnect = (reason: string): void => {
    if (stopped || vaultReceived) return
    closeEventSource()
    clearTimers()

    if (retryCount < TUNNEL_RETRY_MAX) {
      retryCount += 1
      handlers.onStatus?.(`[TUNNEL INTERRUPTED - RETRY ${retryCount}/${TUNNEL_RETRY_MAX}...]`)
      retryTimer = setTimeout(() => connect(), TUNNEL_RETRY_DELAY_MS)
      return
    }

    handlers.onError?.(reason)
  }

  const handleMessage = (raw: string): void => {
    bumpIdleTimeout()
    const msg = parseListenMessage(raw)
    if (!msg) {
      handlers.onError?.('invalid_payload')
      return
    }

    const type = String(msg.type ?? '')
    if (type === 'status') {
      handlers.onStatus?.(String(msg.text ?? ''))
      return
    }

    if (type === 'vault') {
      vaultReceived = true
      clearTimers()
      handlers.onStatus?.('[DOWNLOADING ENCRYPTED DATA...]')
      const encrypted = String(msg.encrypted ?? '').trim()
      const senderPub = String(msg.senderEphemeralPubKey ?? '').trim()
      if (encrypted && senderPub) {
        handlers.onVault?.(encrypted, senderPub)
      } else {
        handlers.onError?.('invalid_vault_payload')
      }
      closeEventSource()
      return
    }

    if (type === 'error') {
      handlers.onError?.(String(msg.message ?? 'pairing_error'))
    }
  }

  const connect = (): void => {
    if (stopped || vaultReceived) return
    closeEventSource()

    const url = resolveSocket2Url(`/device-sync/${encodeURIComponent(sessionId)}/listen`)
    if (retryCount === 0) {
      handlers.onStatus?.('[STABILISHING SECURE TUNNEL...]')
    }

    es = new EventSource(url)

    es.onopen = () => {
      if (stopped || vaultReceived) return
      retryCount = 0
      handlers.onStatus?.('[TUNNEL STABLE - WAITING FOR VAULT...]')
      bumpIdleTimeout()
    }

    es.onmessage = (ev) => {
      if (stopped || vaultReceived) return
      const raw = String(ev.data ?? '').trim()
      if (raw) handleMessage(raw)
    }

    es.onerror = () => {
      if (stopped || vaultReceived) return
      scheduleReconnect('tunnel_lost')
    }
  }

  connect()

  return () => {
    stopped = true
    clearTimers()
    closeEventSource()
  }
}

export async function pushEncryptedVault(
  sessionId: string,
  encryptedB64: string,
  senderEphemeralPubKey: string,
  senderDeviceId: string,
): Promise<boolean> {
  const res = await syncFetch(`/device-sync/${encodeURIComponent(sessionId)}/push`, {
    method: 'POST',
    body: JSON.stringify({
      encrypted: encryptedB64,
      senderEphemeralPubKey,
      senderDeviceId,
    }),
  })
  return res.ok
}

export async function registerLinkedDeviceOnServer(opts: {
  sessionToken: string
  deviceId: string
  label: string
  platform: string
}): Promise<boolean> {
  const res = await syncFetch('/device-sync/register', {
    method: 'POST',
    body: JSON.stringify(opts),
  })
  return res.ok
}

export async function pushHistorySyncToDevice(opts: {
  sessionToken: string
  fromDeviceId: string
  targetDeviceId: string
  encrypted: string
}): Promise<boolean> {
  const res = await syncFetch('/device-sync/history-push', {
    method: 'POST',
    body: JSON.stringify(opts),
  })
  return res.ok
}

export type HistoryPushHandler = (encrypted: string, fromDeviceId: string) => void

let historyPushHandler: HistoryPushHandler | null = null

export function setHistoryPushHandler(handler: HistoryPushHandler | null): void {
  historyPushHandler = handler
}

export function dispatchHistoryPushEvent(data: Record<string, unknown>): boolean {
  const type = String(data.type ?? '').trim().toLowerCase()
  if (type !== 'sync_history_push') return false
  const encrypted = String(data.encrypted ?? data.payload ?? '').trim()
  const fromDeviceId = String(data.fromDeviceId ?? '').trim()
  if (!encrypted) return false
  void (async () => {
    const localId = await import('./deviceLinkStore.ts').then((m) => m.getLocalDeviceId())
    const targetId = String(data.targetDeviceId ?? '').trim()
    if (targetId && targetId !== localId) return
    historyPushHandler?.(encrypted, fromDeviceId)
  })()
  return true
}
